import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ShieldCheck, XCircle, CheckCircle2, Smartphone,
  Users, Calendar, Lock,
} from "lucide-react";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function PublicSupervisorSignature({ kind = "supervisor" }) {
  const { token } = useParams();
  const isSup = kind === "supervisor";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [step, setStep] = useState("start"); // start | code | done
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/${kind}-signature/${token}`);
      setState({ loading: false, error: null, data: r.data });
      if (r.data.status === "signed") setStep("done");
    } catch (e) {
      setState({
        loading: false,
        error: e?.response?.data?.detail || "Link inválido ou expirado",
        data: null,
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sendOtp = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const r = await axios.post(
        `${API}/public/${kind}-signature/${token}/send-otp`,
      );
      setStep("code");
      setFeedback({
        type: "ok",
        msg: `Código enviado por SMS para ${r.data.telefone_masked}`,
      });
    } catch (e) {
      setFeedback({
        type: "err",
        msg: e?.response?.data?.detail || "Falha ao enviar o código",
      });
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await axios.post(
        `${API}/public/${kind}-signature/${token}/confirm`,
        { code: code.trim() },
      );
      setStep("done");
    } catch (e) {
      setFeedback({
        type: "err",
        msg: e?.response?.data?.detail || "Código inválido ou expirado",
      });
    }
    setBusy(false);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="supervisor-sign-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900">Link inválido</h1>
          <p className="text-sm text-slate-500 mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5">
            <ShieldCheck className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-xs font-semibold tracking-wide uppercase">
              AVI Segurança · Assinatura Digital
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="supervisor-sign-card">
          <div className="bg-slate-900 px-6 py-5">
            <h1 className="text-white font-semibold text-lg">
              {isSup
                ? "Designado da CIPA — Supervisor do Contrato"
                : "Sua Carta de Designado da CIPA"}
            </h1>
            <p className="text-slate-400 text-xs mt-1">
              {isSup
                ? "Assinatura única válida para todas as cartas desta lista"
                : "Assine digitalmente a sua carta de designação (NR-05)"}
            </p>
          </div>

          <div className="px-6 py-5 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Lock className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">
                  {isSup ? "Supervisor" : "Designado"}
                </p>
                <p className="font-medium text-slate-900" data-testid="supervisor-sign-name">{d.signer_nome}</p>
              </div>
            </div>
            {isSup && (
              <div className="flex items-center gap-3 text-sm">
                <Users className="w-4 h-4 text-slate-400 shrink-0" />
                <div>
                  <p className="text-[11px] text-slate-400 uppercase tracking-wide">Designados na lista</p>
                  <p className="font-medium text-slate-900">{d.participantes}</p>
                </div>
              </div>
            )}
            {d.training_date && (
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                <div>
                  <p className="text-[11px] text-slate-400 uppercase tracking-wide">Treinamento</p>
                  <p className="font-medium text-slate-900">{d.training_date}</p>
                </div>
              </div>
            )}

            {feedback && (
              <div
                className={`text-xs rounded-lg px-3 py-2 ${
                  feedback.type === "ok"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
                data-testid="supervisor-sign-feedback"
              >
                {feedback.msg}
              </div>
            )}

            {step === "start" && (
              <Button
                className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11"
                onClick={sendOtp}
                disabled={busy}
                data-testid="supervisor-sign-send-otp-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Smartphone className="w-4 h-4 mr-2" />
                )}
                Receber código por SMS ({d.telefone_masked})
              </Button>
            )}

            {step === "code" && (
              <div className="space-y-3">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="Código de 6 dígitos"
                  inputMode="numeric"
                  maxLength={8}
                  className="text-center text-lg tracking-[0.4em] h-12"
                  data-testid="supervisor-sign-code-input"
                />
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                  onClick={confirm}
                  disabled={busy || code.trim().length < 4}
                  data-testid="supervisor-sign-confirm-btn"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  Assinar digitalmente
                </Button>
                <button
                  className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={sendOtp}
                  disabled={busy}
                  data-testid="supervisor-sign-resend-btn"
                >
                  Reenviar código
                </button>
              </div>
            )}

            {step === "done" && (
              <div className="text-center py-4" data-testid="supervisor-sign-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
                <h2 className="font-semibold text-slate-900">
                  Assinatura registrada!
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {isSup
                    ? "Todas as cartas de Designado CIPA desta lista serão emitidas com o seu carimbo digital de auditoria."
                    : "A sua carta de Designado CIPA será emitida com o seu carimbo digital de auditoria."}
                </p>
              </div>
            )}
          </div>

          <div className="bg-slate-50 border-t border-slate-100 px-6 py-3">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Assinatura eletrônica conforme MP nº 2.200-2/2001 e Lei nº
              14.063/2020. IP, dispositivo e data/hora são registrados para
              auditoria.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
